import { Grid, Group, Stack, Tabs } from "@mantine/core"
import { useForm } from "@mantine/form"
import { Button } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useState } from "react"
import z, {
  ZodBoolean,
  ZodDefault,
  ZodEnum,
  ZodNullable,
  ZodNumber,
  ZodObject,
  ZodOptional,
  ZodPipe,
  ZodString,
  ZodType,
  ZodTypeAny,
} from "zod"
// import { $ZodType, $ZodTypeInternals } from "zod/v4/core"

import { DEFAULT_FIELD_RENDERERS } from "./schema-form.renderers"
import type { SchemaFieldMeta, SchemaFormProps } from "./schema-form.types"

const DEFAULT_SPAN = 12

function unwrapZodType(schema: ZodTypeAny): ZodTypeAny {
  if (isZodPipe(schema)) {
    return unwrapZodType(schema._zod.def.out as ZodTypeAny)
  }
  if (
    schema instanceof ZodOptional ||
    schema instanceof ZodNullable ||
    schema instanceof ZodDefault
  ) {
    return unwrapZodType(schema._zod.def.innerType as ZodTypeAny)
  }
  return schema
}

function isZodPipe(schema: ZodTypeAny): schema is ZodPipe {
  return isZodType(schema) && schema._zod.def.type === "pipe"
}

function isZodType(schema: ZodTypeAny): schema is ZodType {
  return schema instanceof ZodType
}

function inferFieldType(schema: ZodTypeAny): string {
  const unwrapped = unwrapZodType(schema)

  if (unwrapped instanceof ZodString) return "text"
  if (unwrapped instanceof ZodNumber) return "number"
  if (unwrapped instanceof ZodBoolean) return "boolean"
  if (unwrapped instanceof ZodEnum) return "select"

  return "text"
}

function getEnumOptions(
  schema: ZodTypeAny
): ReadonlyArray<{ readonly label: string; readonly value: string }> {
  const unwrapped = unwrapZodType(schema)
  if (unwrapped instanceof ZodEnum) {
    const entries = unwrapped._zod.def.entries as Record<string, string>
    return Object.values(entries).map((value) => ({
      label: value,
      value,
    }))
  }
  return []
}

function hasFieldError({
  fieldKey,
  errorKeys,
}: {
  readonly fieldKey: string
  readonly errorKeys: readonly string[]
}): boolean {
  return errorKeys.some(
    (errorKey) =>
      errorKey === fieldKey ||
      errorKey.startsWith(`${fieldKey}.`) ||
      errorKey.startsWith(`${fieldKey}[`)
  )
}

export function SchemaForm<T extends ZodObject<Record<string, ZodTypeAny>>>({
  schema,
  initialValues,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
  isLoading,
  fieldRenderers,
  translateError,
  deleteLabel,
  handleDelete,
  tabs,
}: SchemaFormProps<T>): JSX.Element {
  const form = useForm({
    validate: zod4Resolver(schema),
    initialValues,
    validateInputOnChange: true,
  })
  const [activeTabKey, setActiveTabKey] = useState<string | null>(
    tabs?.[0]?.key ?? null
  )

  const allRenderers = { ...DEFAULT_FIELD_RENDERERS, ...fieldRenderers }
  const shape = schema.shape as Record<string, ZodTypeAny>

  const handleSubmit = (values: z.infer<T>): void => {
    onSubmit(values)
  }

  const handleCancel = (): void => {
    form.reset()
    onCancel?.()
  }

  const resolvedActiveTabKey =
    tabs && tabs.length > 0
      ? tabs.some((tab) => tab.key === activeTabKey)
        ? activeTabKey
        : tabs[0].key
      : null

  const switchToFirstErrorTab = ({
    errors,
  }: {
    readonly errors: Record<string, unknown>
  }): void => {
    if (!tabs || tabs.length === 0) {
      return
    }

    const errorKeys = Object.keys(errors)
    if (errorKeys.length === 0) {
      return
    }

    for (const tab of tabs) {
      for (const fieldKey of tab.fields) {
        if (hasFieldError({ fieldKey, errorKeys })) {
          setActiveTabKey(tab.key)
          return
        }
      }
    }
  }

  const renderFields = (fieldKeys: ReadonlySet<string>): JSX.Element => (
    <Grid gutter="sm">
      {Object.entries(shape)
        .filter(([key]) => fieldKeys.has(key))
        .map(([key, fieldSchema]) => {
          const meta = (fieldSchema.meta?.() ??
            {}) as unknown as SchemaFieldMeta
          const fieldType = meta.fieldType ?? inferFieldType(fieldSchema)
          const renderer = allRenderers[fieldType]

          if (!renderer) {
            return null
          }

          const label = meta.label ?? key
          const options = meta.options ?? getEnumOptions(fieldSchema)
          const rawError = form.errors[key] as string | undefined
          const error =
            rawError && translateError ? translateError(rawError) : rawError
          return (
            <Grid.Col key={key} span={meta.span ?? DEFAULT_SPAN}>
              {renderer({
                name: key,
                label,
                placeholder: meta.placeholder,
                tooltip: meta.tooltip,
                isRequired: meta.isRequired ?? false,
                disabled: meta.disabled,
                options: options.length > 0 ? options : undefined,
                error,
                form,
              })}
            </Grid.Col>
          )
        })}
    </Grid>
  )

  const allKeys = new Set(Object.keys(shape))
  const formContent = tabs ? (
    <Tabs value={resolvedActiveTabKey} onChange={setActiveTabKey}>
      <Tabs.List>
        {tabs.map((tab) => (
          <Tabs.Tab key={tab.key} value={tab.key}>
            {tab.label}
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {tabs.map((tab) => (
        <Tabs.Panel
          key={tab.key}
          value={tab.key}
          px="var(--tfg-layout-padding-x)"
          py="var(--tfg-layout-padding-y)"
        >
          {renderFields(new Set(tab.fields))}
        </Tabs.Panel>
      ))}
    </Tabs>
  ) : (
    renderFields(allKeys)
  )

  return (
    <form
      onSubmit={form.onSubmit(
        (values) => handleSubmit(values as z.infer<T>),
        (errors) => switchToFirstErrorTab({ errors })
      )}
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Stack gap="md" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {formContent}
      </Stack>
      <Group
        justify="space-between"
        pt="md"
        style={{
          flexShrink: 0,
          borderTop: "1px solid var(--mantine-color-gray-1)",
        }}
      >
        {deleteLabel && handleDelete && (
          <Button variant="tertiary-critical" onClick={handleDelete}>
            {deleteLabel}
          </Button>
        )}
        <Group gap="xs" style={{ justifySelf: "end" }}>
          {onCancel && (
            <Button variant="tertiary" onClick={handleCancel}>
              {cancelLabel ?? "Cancel"}
            </Button>
          )}
          <Button type="submit" loading={isLoading}>
            {submitLabel}
          </Button>
        </Group>
      </Group>
    </form>
  )
}
